import { Button, Dialog, DynamicContainer, Form, FormContext } from '@hai/ui-react';
import { FormikProps } from 'formik';
import { isNil } from 'ramda';
import React, { ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import { constant } from 'src/js/constant';
import { validateFormContext } from 'src/js/validator/validator';

import {
  MetadataCoTRelay,
  MetadataTypeEnum,
  getTOS,
  getTOSHint,
  getTTL,
  getTTLHint,
  metadataCoTRelayValidationSchema,
} from './metadata-models';

interface Props {
  onCancel: VoidFunction;
  onApply?: (relay: MetadataCoTRelay) => void;
  data: MetadataCoTRelay;
  type: MetadataTypeEnum;
  edit: boolean;
}

export const MetadataCoTRelayModal: React.FunctionComponent<Props> = ({ onCancel, onApply, data, type, edit }) => {
  const { t } = useTranslation();

  const handleSubmit = (values: MetadataCoTRelay) => {
    onApply(values);
  };

  const nameInputRef = useRef(undefined);
  useEffect((): void => {
    if (!isNil(nameInputRef.current)) {
      nameInputRef.current.focus();
    }
  }, [nameInputRef]);

  useEffect(() => {
    data.ttl = getTTL(data.ttl);
    data.tos = getTOS(data.tos);
  }, []);

  const content = (): ReactNode => {
    const formRef = useRef<FormikProps<MetadataCoTRelay>>();

    return (
      <Form
        initialValues={data}
        defaultValidation={true}
        handleSubmit={handleSubmit}
        restValidationProps={{
          enableReinitialize: true,
          validationSchema: metadataCoTRelayValidationSchema(t),
          innerRef: formRef,
        }}
      >
        <FormContext.Consumer>
          {(formContext: any) => {
            const values = formContext.values;
            return (
              <>
                <DynamicContainer minColumns={2} maxColumns={2}>
                  <FormTextfield name="ip" title={t('metadata.settings.address')} inputRef={nameInputRef} required />
                  <FormTextfield name="port" title={t('metadata.settings.port')} type="number" required />
                </DynamicContainer>
                <DynamicContainer minColumns={2} maxColumns={2}>
                  <FormTextfield
                    name="ttl"
                    title={t('metadata.settings.ttl')}
                    hint={getTTLHint(values.ttl, type, true, t)}
                    type="number"
                  />
                  <FormTextfield
                    name="tos"
                    title={t('metadata.settings.tos')}
                    hint={getTOSHint(values.tos, type, true, t)}
                  />
                </DynamicContainer>
                <div className="d-flex justify-content-end hai-mt-6">
                  <Button onClick={onCancel}>{t('general.cancel')}</Button>
                  <Button
                    onClick={() => validateFormContext(formContext, t)}
                    disabled={!formContext.dirty}
                    variant="primary"
                  >
                    {isNil(data.index) ? t('general.add') : t('general.saveBtn')}
                  </Button>
                </div>
              </>
            );
          }}
        </FormContext.Consumer>
      </Form>
    );
  };

  const handleOnKeyUpInnerModal = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Enter') {
      // to prevent from submitting the parent form
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Dialog
      accentColor={constant.productColor}
      title={edit ? t('metadata.settings.modal.cotRelay.editTitle') : t('metadata.settings.modal.cotRelay.addTitle')}
      onClose={onCancel}
      show={true}
      dialogType="activity"
      headerIcon="Metadata"
      content={content()}
      onKeyUp={handleOnKeyUpInnerModal}
    />
  );
};
